import { FC, useCallback, useContext, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import {
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  darken,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  styled,
} from '@mui/material';
import { BoltOutlined, Close } from '@mui/icons-material';
import {
  AuthenticationContext,
  markPostAsRead,
  Post,
} from '@vs-online-only-app/state';
import { markdownOptions } from '@vs-online-only-app/ui';
import PostMedia from './PostMedia';
import PostTitle from './PostTitle';
import PostAuthor from './PostAuthor';

export const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(2)};
  right: ${({ theme }) => theme.spacing(2)};
  z-index: 20;
  background-color: ${({ theme }) => theme.palette.common.white};
  color: ${({ theme }) => theme.palette.primary.main};
  box-shadow: ${({ theme }) => theme.shadows[1]};

  &:hover {
    background-color: ${({ theme }) => darken(theme.palette.common.white, 0.1)};
  }
`;

const StyledChip = styled(Chip)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(2)};
  left: ${({ theme }) => theme.spacing(2)};
  z-index: 20;
  box-shadow: ${({ theme }) => theme.shadows[1]};
`;

const NewChip: FC = () => (
  <StyledChip
    label={
      <Stack direction="row" alignItems="center">
        <BoltOutlined fontSize="inherit" />
        New
      </Stack>
    }
    size="small"
  />
);

export const CARD_HEIGHT = 280;

const PostCard: FC<Post> = ({
  _id,
  analytics,
  title,
  body,
  publishedAt,
  media,
  author,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasViewedPost, setHasViewedPost] = useState(false);

  const { user } = useContext(AuthenticationContext);

  const markAsRead = useCallback(async () => {
    try {
      await markPostAsRead(_id);
    } catch (error) {
      console.error(error);
    }
  }, [_id]);

  const onClose = () => {
    setIsModalOpen(false);
    setHasViewedPost(true);
    markAsRead();
  };
  const open = () => setIsModalOpen(true);

  const hasBeenRead =
    hasViewedPost || (user && analytics?.readBy.includes(user.id));

  return (
    <>
      <Dialog open={isModalOpen} onClose={onClose} fullWidth maxWidth="md">
        {!hasBeenRead && <NewChip />}
        <CloseButton onClick={onClose} size="small">
          <Close fontSize="small" />
        </CloseButton>
        <PostMedia media={media} />
        <PostTitle title={title} publishedAt={publishedAt} />
        <DialogContent>
          <Stack gap={2}>
            <Markdown options={markdownOptions}>{body}</Markdown>
            <PostAuthor {...author} />
          </Stack>
        </DialogContent>
      </Dialog>
      <Card
        onClick={open}
        sx={(theme) => ({
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          height: CARD_HEIGHT,
        })}
      >
        <CardActionArea>
          {!hasBeenRead && <NewChip />}
          <CardMedia>
            <PostMedia media={media} isPreview />
          </CardMedia>
          <PostTitle title={title} publishedAt={publishedAt} />
        </CardActionArea>
      </Card>
    </>
  );
};

export default PostCard;
